/**
 * AutoGlow · Advisor · Reusable badges and pills.
 * StatusBadge · PriorityPill · ImpactPill · EffortPill · ConfidenceBadge · CitationBadge.
 *
 * All pure presentational components · NO logic.
 */
import React from "react";
import { Info } from "lucide-react";

// ---------------------------------------------------------------------------
// Generic status badge · color encodes status semantically
// ---------------------------------------------------------------------------

const STATUS_TONE = {
  excelente: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
  bueno: "bg-green-500/15 text-green-500 border-green-500/30",
  medio: "bg-yellow-500/15 text-yellow-500 border-yellow-500/30",
  bajo: "bg-orange-500/15 text-orange-500 border-orange-500/30",
  critico: "bg-red-500/15 text-red-500 border-red-500/30",
  indeterminado: "bg-slate-500/15 text-slate-500 border-slate-500/30",
};

export function StatusBadge({ status, label, testId }) {
  const tone = STATUS_TONE[status] || STATUS_TONE.indeterminado;
  return (
    <span
      data-testid={testId || `status-badge-${status}`}
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${tone}`}
    >
      {label || status}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Priority pill · P0/P1/P2/P3
// ---------------------------------------------------------------------------

const PRIORITY_TONE = {
  P0: "bg-red-500/15 text-red-500 border-red-500/30",
  P1: "bg-orange-500/15 text-orange-500 border-orange-500/30",
  P2: "bg-blue-500/15 text-blue-500 border-blue-500/30",
  P3: "bg-slate-500/15 text-slate-500 border-slate-500/30",
};

export function PriorityPill({ priority, testId }) {
  const tone = PRIORITY_TONE[priority] || PRIORITY_TONE.P3;
  return (
    <span
      data-testid={testId || `priority-${priority}`}
      className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-semibold ${tone}`}
    >
      {priority}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Impact / Effort / Confidence pills · share a common renderer
// ---------------------------------------------------------------------------

const LABEL_TONE = {
  muy_alto: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
  alto: "bg-green-500/15 text-green-500 border-green-500/30",
  medio: "bg-yellow-500/15 text-yellow-500 border-yellow-500/30",
  bajo: "bg-slate-500/15 text-slate-500 border-slate-500/30",
  baja: "bg-slate-500/15 text-slate-500 border-slate-500/30",
  media: "bg-yellow-500/15 text-yellow-500 border-yellow-500/30",
  alta: "bg-green-500/15 text-green-500 border-green-500/30",
  critica: "bg-red-500/15 text-red-500 border-red-500/30",
};

export function LevelPill({ value, prefix, testId }) {
  const tone = LABEL_TONE[value] || "bg-slate-500/15 text-slate-500 border-slate-500/30";
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${tone}`}
    >
      {prefix ? `${prefix}: ${value}` : value}
    </span>
  );
}

export const ImpactPill = ({ impact, testId }) => (
  <LevelPill value={impact} prefix="impact" testId={testId || `impact-${impact}`} />
);

export const EffortPill = ({ effort, testId }) => (
  <LevelPill value={effort} prefix="esfuerzo" testId={testId || `effort-${effort}`} />
);

export const ConfidenceBadge = ({ confidence, testId }) => (
  <LevelPill value={confidence} prefix="confianza" testId={testId || `confidence-${confidence}`} />
);

// ---------------------------------------------------------------------------
// Forecast band · 0 / + / ++ / +++
// ---------------------------------------------------------------------------

const BAND_TONE = {
  "0": "bg-slate-500/15 text-slate-500 border-slate-500/30",
  "+": "bg-yellow-500/15 text-yellow-500 border-yellow-500/30",
  "++": "bg-green-500/15 text-green-500 border-green-500/30",
  "+++": "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
};

export function BandBadge({ band, label, testId }) {
  const tone = BAND_TONE[band] || BAND_TONE["0"];
  return (
    <span
      data-testid={testId || `band-${band}`}
      className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-medium ${tone}`}
    >
      {label || band}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Citation badge · the "[ag02:funnel]" tag below every widget · grounding
// ---------------------------------------------------------------------------

export function CitationBadge({ sources, testId }) {
  if (!sources || sources.length === 0) return null;
  const visible = sources.slice(0, 3);
  const more = sources.length - visible.length;
  return (
    <div
      data-testid={testId || "citation-badge"}
      className="mt-2 flex flex-wrap items-center gap-1"
    >
      <Info className="h-3 w-3 text-[var(--ag-text-dim)]" aria-hidden="true" />
      {visible.map((s, i) => (
        <span
          key={`${s}-${i}`}
          className="rounded bg-[var(--ag-surface-2)] px-1.5 py-0.5 text-[10px] text-[var(--ag-text-dim)]"
        >
          {s}
        </span>
      ))}
      {more > 0 && (
        <span className="text-[10px] text-[var(--ag-text-dim)]">+{more}</span>
      )}
    </div>
  );
}
